import { Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Monitor, Menu, UserCircle, LogOut, User } from 'lucide-react';
import Sidebar from './Sidebar';
import MobileDock from './MobileDock';
import BrandPresetSwitcher from './BrandPresetSwitcher';
import { api, setActiveTenantId as apiSetActiveTenantId, getActiveTenantId } from '../../lib/api';
import { useTheme, type ThemeMode } from '../../lib/useTheme';

interface TenantSummary { id: string; name: string; slug: string; domain: string | null }

const THEME_CYCLE: ThemeMode[] = ['system', 'light', 'dark'];
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon };
const THEME_LABEL = { system: 'Sistema', light: 'Claro', dark: 'Oscuro' };

export default function Layout() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [isSaasAdmin, setIsSaasAdmin] = useState(false);
  const [tenantList, setTenantList] = useState<TenantSummary[]>([]);
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(getActiveTenantId());
  const [activeTenantName, setActiveTenantName] = useState<string | null>(null);
  const { mode, setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  function cycleTheme() {
    const next = THEME_CYCLE[(THEME_CYCLE.indexOf(mode) + 1) % THEME_CYCLE.length];
    setTheme(next);
  }

  useEffect(() => {
    api.auth.me().then(({ user }) => {
      setEmail(user?.email ?? null);
      if (!user) return;

      api.settings.getSite().then((site) => {
        setActiveTenantName(site?.name ?? null);
      }).catch(() => {
        setActiveTenantName(null);
      });

      if (user.role === 'saas_admin') {
        setIsSaasAdmin(true);
        api.tenants.list().then((list) => {
          setTenantList(list);
          // Si no hay override activo pero el user tiene tenantId, inicializarlo
          if (!getActiveTenantId() && user.tenantId) {
            apiSetActiveTenantId(user.tenantId);
            setActiveTenantIdState(user.tenantId);
          }
        }).catch(() => {});
      }
    }).catch(() => null);
  }, []);

  function handleTenantSwitch(id: string) {
    apiSetActiveTenantId(id);
    window.location.href = '/';
  }

  async function handleLogout() {
    apiSetActiveTenantId(null);
    await api.auth.logout().catch(() => null);
    navigate(0);
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-ui-bg">
      <Sidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        isSaasAdmin={isSaasAdmin}
        tenants={tenantList}
        activeTenantId={activeTenantId}
        activeTenantName={activeTenantName}
        onTenantSwitch={handleTenantSwitch}
      />

      <div className="relative z-0 flex flex-col flex-1 overflow-hidden min-w-0">
        <header className="relative z-30 h-14 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-ui-border bg-ui-surface/60 backdrop-blur-sm">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
            aria-label="Abrir menú"
          >
            <Menu size={18} />
          </button>

          <div className="flex items-center gap-3 ml-auto">
            <BrandPresetSwitcher />
            <button
              onClick={cycleTheme}
              title={`Tema: ${THEME_LABEL[mode]}`}
              className="relative w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors overflow-hidden"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={mode}
                  initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.6, rotate: 30 }}
                  transition={{ duration: 0.18 }}
                  className="absolute"
                >
                  {(() => { const Icon = THEME_ICON[mode]; return <Icon size={15} />; })()}
                </motion.span>
              </AnimatePresence>
            </button>

            {email && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-ui-accent/15 text-ui-accent hover:bg-ui-accent/25 transition-colors"
                  aria-label="Menú de usuario"
                >
                  <UserCircle size={18} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.13 }}
                      className="absolute right-0 top-10 w-56 bg-ui-surface border border-ui-border rounded-xl shadow-xl z-50 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-ui-border">
                        <p className="text-xs text-ui-text-subtle">Sesión activa</p>
                        <p className="text-sm text-ui-text font-medium truncate mt-0.5">{email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate('/account'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
                        >
                          <User size={15} className="shrink-0" />
                          Mi perfil
                        </button>
                        <button
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut size={15} className="shrink-0" />
                          Cerrar sesión
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-24 md:pb-6 bg-ui-bg">
          <Outlet />
        </main>

        <MobileDock onOpenMore={() => setMobileNavOpen(true)} />
      </div>
    </div>
  );
}
